// VehiclePDFView.tsx
import React from "react";
import { Button } from "./ui/button";
import { X } from "lucide-react";

interface VehicleData {
  registration_type: string;
  mt: string;
  id: string;
  date: string;
  registration_number: string;
  owner_name: string;
  mobile: string;
  alternate_mobile?: string;
  district: string;
  imei: string;
  sim_number1: string;
  sim_number2: string;
  sim_start: string;
  sim_end: string;
  rc_copy_path: string;
  id_proof_path: string;
  status: string;
}

interface Props {
  data: VehicleData;
  setPreviewData: (data: VehicleData | null) => void;
  sim_start?: string;
  sim_end?: string;
  ref?: React.Ref<HTMLDivElement>;
}

const VehiclePDFView = React.forwardRef<HTMLDivElement, Props>(
  ({ data, sim_start, sim_end, setPreviewData }, ref) => {
    const formatDate = (date: string) => {
      if (!date) return "";
      const d = new Date(date);
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();
      return `${day}-${month}-${year}`;
    };

    return (
      <div ref={ref} className="p-6 w-[800px] mx-auto text-sm">
        <div className="flex items-center justify-between mb-10">
          <h2 className="text-base italic font-bold text-center w-full">
            One State One GPS Project – Department of Mines & Geology
          </h2>
          {typeof setPreviewData === "function" && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setPreviewData(null)}
              className="h-8 w-8 text-muted-foreground hover:text-foreground absolute right-6 top-6"
              style={{ position: "absolute", right: 36, top: 24 }}
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
        <p className="text-center text-base mb-0 font-bold">Application Form</p>
        <p className="text-center mb-0">
          Application form for providing AIS-140 Device for Integration
        </p>
        <p className="text-center mb-7">
          Purpose - for "One State One GPS Project - Karnataka State"
        </p>
        <div className="flex flex-col items-end">
          <p className="font-bold mb-7">
            Date: <strong>{formatDate(data.date)}</strong>
          </p>
        </div>
        <div className="flex flex-row justify-between mb-2">
          <div className="flex flex-col items-start">
            <p className="mb-0">To,</p>
            <p className="mb-0">Device Integration Team</p>
            <p className="mb-0">One State One GPS Control Room</p>
            <p className="mb-0">Department of Mines and Geology</p>
          </div>
        </div>
        <div className="flex flex-col items-end">
          <p className="mb-4">
            District Name: <strong>{data.district}</strong>
          </p>
        </div>
        <p className="mb-4">
          <strong>Subject:</strong> Providing AIS-140 Device for integration
          purpose – for Karnataka State “One State One GPS Project”
        </p>
        <div className="flex flex-row justify-between mb-2">
          <div className="flex flex-col items-start">
            <p className="mx-8">Sir,</p>
            <p className="mb-2">
              I have fully understood the requirements and the process of
              integration of AIS-140 Device - for Karnataka State "One GPS
              Project and I am Providing my AIS-140 Device along with the
              required details
            </p>
          </div>
        </div>
        <table className="w-full text-left border-2 border-black border-collapse table-fixed">
          <colgroup>
            <col style={{ width: "50%" }} />
            <col style={{ width: "50%" }} />
          </colgroup>
          <tbody>
            <tr>
              <td className="border-2 border-black p-1 pb-4 text-center">
                District
              </td>
              <td className="border-2 border-black p-1 pb-4 text-center">
                {data.district}
              </td>
            </tr>
            <tr>
              <td className="border-2 border-black p-1 pb-4 text-center">
                Vehicle Registration No
              </td>
              <td className="border-2 border-black p-1 pb-4 text-center">
                {data.registration_number}
              </td>
            </tr>
            <tr>
              <td className="border-2 border-black p-1 pb-4 text-center">
                Vehicle Owner Name
              </td>
              <td className="border-2 border-black p-1 pb-4 text-center">
                {data.owner_name}
              </td>
            </tr>
            <tr>
              <td className="border-2 border-black p-1 pb-4 text-center">
                Mobile No
              </td>
              <td className="border-2 border-black p-1 pb-4 text-center">
                {data.mobile}
              </td>
            </tr>
            <tr>
              <td className="border-2 border-black p-1 pb-4 text-center">
                AIS-140 GPS device Manufacturer
              </td>
              <td className="border-2 border-black p-1 pb-4 text-center">
                CRG TRACKER
              </td>
            </tr>
            <tr>
              <td className="border-2 border-black p-1 pb-4 text-center">
                AIS-140 device model
              </td>
              <td className="border-2 border-black p-1 pb-4 text-center">
                CRG TRACKER
              </td>
            </tr>
            <tr>
              <td className="border-2 border-black p-1 pb-4 text-center">
                IMEI
              </td>
              <td className="border-2 border-black p-1 pb-4 text-center">
                {data.imei}
              </td>
            </tr>
            <tr>
              <td className="border-2 border-black p-1 pb-4 text-center">
                SIM Airtel Number
              </td>
              <td className="border-2 border-black p-1 pb-4 text-center">
                {data.sim_number1}
              </td>
            </tr>
            <tr>
              <td className="border-2 border-black p-1 pb-4 text-center">
                SIM BSNL Number
              </td>
              <td className="border-2 border-black p-1 pb-4 text-center">
                {data.sim_number2}
              </td>
            </tr>
            <tr>
              <td className="border-2 border-black p-1 pb-4 text-center">
                SIM Validity Start
              </td>
              <td className="border-2 border-black p-1 pb-4 text-center">
                {formatDate(sim_start)}
              </td>
            </tr>
            <tr>
              <td className="border-2 border-black p-1 pb-4 text-center">
                SIM Validity End
              </td>
              <td className="border-2 border-black p-1 pb-4 text-center">
                {formatDate(sim_end)}
              </td>
            </tr>
            <tr>
              <td className="border-2 border-black p-1 pb-4 text-center">
                Device Password
              </td>
              <td className="border-2 border-black p-1 pb-4 text-center">
                Not Set
              </td>
            </tr>
            <tr>
              <td className="border-2 border-black p-1 pb-4 text-center">
                Configuration tool and Login Credentials
              </td>
              <td className="border-2 border-black p-1 pb-4 text-center">
                Not Set
              </td>
            </tr>
            <tr>
              <td className="border-2 border-black p-1 pb-4 text-center">
                Device Seller Mobile
              </td>
              <td className="border-2 border-black p-1 pb-4 text-center"></td>
            </tr>
            <tr>
              <td className="border-2 border-black p-1 pb-4 text-center">
                Vehicle RC Copy
              </td>
              <td className="border-2 border-black p-1 pb-4 text-center">
                to be attached with this form
              </td>
            </tr>
            <tr>
              <td className="border-2 border-black p-1 pb-4 text-center">
                Owner ID Proof
              </td>
              <td className="border-2 border-black p-1 pb-4 text-center">
                to be attached with this form
              </td>
            </tr>
          </tbody>
        </table>
        <p className="mx-4 my-4">
          I hereby provide my AIS-140 Devices for Integration with the following
          details/Acknowledgement:
        </p>
        <div className="flex flex-row justify-between mb-2">
          <div className="flex flex-col items-start">
            <p className="mb-1">
              1) All Information provided in the application form is true and
              correct.
            </p>
            <p className="mb-1">
              2) I ensure that the GPS Device provided by me is compliance with
              AIS-140 standards.
            </p>
          </div>
        </div>
        <div className="flex justify-end">
          <div className="mt-16 mb-8 mr-36 text-right">
            <div className="mb-1 text-left">Sincerely,</div>
            <div className="mb-1 text-left">Name:</div>
            <div className="text-left">Signature:</div>
          </div>
        </div>
      </div>
    );
  }
);

export default VehiclePDFView;
